// Graceful fallback (and best-effort auto-repair) for when mermaid can't render
// a diagram. Wired into markstream's MermaidBlockNode via `mermaidProps.onRenderError`
// in BOTH apps, so the behaviour is identical everywhere.
//
// markstream's default on a render failure is a bare red "Failed to render
// diagram: <error>" - the whole diagram is lost. Instead we:
//   1. immediately drop the raw source to a code block (preserved + copyable),
//      under a calm muted note, with the parser error on hover; then
//   2. asynchronously try an auto-repair: quote the unquoted labels that trip
//      the grammar (autoFixMermaid) and re-render. If the repaired text renders,
//      we swap the diagram in; if it still won't parse we keep the source.
//
// The repair is SAFE because it only runs AFTER a diagram already failed, and we
// re-render before swapping - so it can never break a diagram that was fine; the
// worst case is the source block we already showed. Returning `true` tells
// markstream we own the container (verified against markstream-vue@1.0.1
// dist/index11.js: `onRenderError(e, code, container) === true` skips the default
// error div).
import { useI18n } from './i18n';
import { autoFixMermaid } from './mermaidAutoFix';

// Theme tokens differ across surfaces: web-ui uses --font-mono / --surface-* /
// --ink-muted, the extension mirrors them as --hq-*. Chain web-ui's name, then
// the extension's, then a literal, so the fallback themes correctly in either
// app with zero per-app CSS.
const MONO = 'var(--font-mono, var(--hq-mono, ui-monospace, SFMono-Regular, Menlo, monospace))';
const SUNKEN = 'var(--surface-sunken, var(--hq-sunken, rgba(128,128,128,0.08)))';
const BORDER = 'var(--border-subtle, var(--hq-border, rgba(128,128,128,0.28)))';
const MUTED = 'var(--ink-muted, var(--hq-muted, currentColor))';
const RADIUS = 'var(--radius-card, 8px)';

// Minimal shape of the mermaid API we use. We never import mermaid here (it
// lives in the host app's node_modules, not the package's); instead the app
// injects a loader via configureMermaidRepair() - same DI pattern as
// configureLocale / provideIcons. Without it, the fallback just shows the source.
interface MermaidLike {
  initialize: (config: Record<string, unknown>) => void;
  render: (id: string, text: string) => Promise<{ svg: string }>;
}
type MermaidLoader = () => Promise<unknown>;

let mermaidLoader: MermaidLoader | null = null;
let repairSeq = 0;

/** App entry point wires this with `() => import('mermaid')` so the auto-repair
 *  can re-render a fixed diagram. Optional: unset = source-only fallback.
 *  The same loader powers HqMermaid (our `setCustomComponents({ mermaid })`
 *  override), so one config call serves both. */
export function configureMermaidRepair(loader: MermaidLoader | null): void {
  mermaidLoader = loader;
}

/** Load + normalize the mermaid module via the configured loader. Returns null if
 *  no loader is wired or the import fails. Shared by attemptRepair() and HqMermaid. */
export async function getMermaid(): Promise<MermaidLike | null> {
  if (!mermaidLoader) return null;
  try {
    const mod = await mermaidLoader();
    return (((mod as { default?: unknown }).default ?? mod) as MermaidLike);
  } catch {
    return null;
  }
}

/** Render the raw source as a code block (the always-available fallback). */
function renderSource(error: unknown, code: string, container: HTMLElement): void {
  const { t } = useI18n();
  const detail = error instanceof Error ? error.message : String(error ?? '');

  const wrap = document.createElement('div');
  wrap.style.cssText = 'display:flex;flex-direction:column;gap:0.4rem;';

  const note = document.createElement('p');
  note.textContent = t('mermaid.render_failed');
  note.style.cssText = `margin:0;font-size:0.8125rem;color:${MUTED};`;
  if (detail) note.title = detail; // full parser error on hover; console keeps the rest
  wrap.appendChild(note);

  if (code) {
    const pre = document.createElement('pre');
    pre.style.cssText = `margin:0;padding:0.6rem 0.75rem;overflow:auto;background:${SUNKEN};border:1px solid ${BORDER};border-radius:${RADIUS};`;
    const codeEl = document.createElement('code');
    codeEl.textContent = code;
    codeEl.style.cssText = `font-family:${MONO};font-size:0.8125rem;white-space:pre;`;
    pre.appendChild(codeEl);
    wrap.appendChild(pre);
  }

  container.replaceChildren(wrap);
}

/** Try to quote the broken labels and re-render; swap the diagram in on success. */
async function attemptRepair(code: string, container: HTMLElement): Promise<void> {
  if (!mermaidLoader) return; // no mermaid wired - source-only fallback
  const fixed = autoFixMermaid(code);
  if (fixed === code) return; // heuristic found nothing to change

  let svg: string;
  try {
    const mod = await mermaidLoader();
    const mermaid = (((mod as { default?: unknown }).default ?? mod) as MermaidLike);
    const dark = document.documentElement.dataset.theme === 'dark';
    // Our own instance (markstream renders in a worker), so theming it here
    // doesn't touch markstream's. strict = sanitize the agent-authored content.
    mermaid.initialize({ startOnLoad: false, theme: dark ? 'dark' : 'default', securityLevel: 'strict' });
    const out = await mermaid.render(`hq-mmd-repair-${(repairSeq += 1)}`, fixed);
    svg = out.svg;
  } catch {
    return; // mermaid unavailable, or the repaired text still won't parse
  }
  if (!svg) return;

  const holder = document.createElement('div');
  holder.className = 'hq-mmd-repaired';
  holder.style.cssText = 'max-width:100%;overflow:auto;';
  holder.innerHTML = svg;
  container.replaceChildren(holder);
}

/** markstream `onRenderError`: show the source, then try to auto-repair. */
export function renderMermaidFallback(error: unknown, code: string, container: HTMLElement): boolean {
  if (typeof document === 'undefined' || !container) return false;
  renderSource(error, code, container);
  void attemptRepair(code, container);
  return true;
}
